import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import {
	type StatusBarItem,
	useWorkspacePage,
} from "@/components/workspace";
import {
	useClosePeriod,
	useGetCloseChecklist,
	useReopenPeriod,
	useRunCloseAgent,
} from "@/hooks/use-accounting";
import { useBreadcrumbs } from "@/lib/use-breadcrumbs";
import { createFileRoute } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import {
	CheckCircle2,
	CircleDashed,
	Loader2,
	SkipForward,
	XCircle,
} from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/accounting/close/$periodId")({
	component: PeriodCloseDetailPage,
});

const STATUS_ICONS: Record<string, React.ReactNode> = {
	pending: <CircleDashed className="h-4 w-4 text-muted-foreground" />,
	running: <Loader2 className="h-4 w-4 animate-spin text-info" />,
	completed: <CheckCircle2 className="h-4 w-4 text-positive" />,
	failed: <XCircle className="h-4 w-4 text-negative" />,
	skipped: <SkipForward className="h-4 w-4 text-muted-foreground" />,
};

const STATUS_KEYS: Record<string, string> = {
	pending: "close_status_pending",
	running: "close_status_running",
	completed: "close_status_completed",
	failed: "close_status_failed",
	skipped: "close_status_skipped",
};

function PeriodCloseDetailPage() {
	const { t } = useTranslation("accounting");
	const { periodId } = Route.useParams();
	const { data: checklist, isLoading } = useGetCloseChecklist(periodId);
	const runClose = useRunCloseAgent();
	const closePeriod = useClosePeriod();
	const reopenPeriod = useReopenPeriod();
	const [confirmOpen, setConfirmOpen] = useState(false);

	useBreadcrumbs([
		{ label: t("period_close"), href: "/accounting/close" },
		{ label: periodId },
	]);

	const isClosed = checklist?.isComplete;
	const stepsTotal = checklist?.steps.length ?? 0;
	const stepsDone =
		checklist?.steps.filter(
			(s) => s.status === "completed" || s.status === "skipped",
		).length ?? 0;
	const checklistSteps = checklist?.steps ?? [];
	type ChecklistStep = (typeof checklistSteps)[number];
	const checklistCols = useMemo<ColumnDef<ChecklistStep>[]>(
		() => [
			{
				id: "icon",
				header: "",
				cell: ({ row }) =>
					STATUS_ICONS[row.original.status] ?? STATUS_ICONS.pending,
				size: 36,
			},
			{
				accessorKey: "name",
				header: t("close_task_name"),
				cell: ({ row }) => (
					<span className="font-medium">{row.original.name || "—"}</span>
				),
			},
			{
				accessorKey: "status",
				header: t("close_task_status"),
				cell: ({ row }) => (
					<Badge variant="outline" className="text-xs">
						{t(STATUS_KEYS[row.original.status] ?? "close_status_pending")}
					</Badge>
				),
			},
			{
				accessorKey: "entriesCreated",
				header: () => (
					<span className="block text-right">{t("close_task_entries")}</span>
				),
				cell: ({ row }) => (
					<span className="block text-right tabular-nums">
						{row.original.entriesCreated || "—"}
					</span>
				),
			},
			{
				accessorKey: "error",
				header: t("close_task_error"),
				cell: ({ row }) => (
					<span className="text-xs text-negative">
						{row.original.error || ""}
					</span>
				),
			},
		],
		[t],
	);

	const statusBarItems = useMemo<StatusBarItem[]>(
		() => [
			{
				id: "progress",
				content: `${stepsDone}/${stepsTotal} ${t("close_tasks_done")}`,
				align: "left" as const,
			},
		],
		[stepsDone, stepsTotal, t],
	);

	useWorkspacePage({ statusBar: statusBarItems });

	return (
		<div className="flex flex-col h-full">
			<div className="flex items-center gap-2 px-4 py-2 shrink-0">
				<div className="flex-1" />
				<Button
					variant="outline"
					size="sm"
					className="h-9"
					onClick={() => reopenPeriod.mutate({ periodId, reason: "" })}
					disabled={reopenPeriod.isPending}
				>
					{t("close_reopen", { defaultValue: "Переоткрыть" })}
				</Button>
				<Button
					variant="outline"
					size="sm"
					className="h-9"
					onClick={() => setConfirmOpen(true)}
					disabled={!isClosed}
				>
					{t("close_finalize")}
				</Button>
				<Button
					size="sm"
					className="h-9"
					onClick={() => runClose.mutate({ periodId })}
					disabled={runClose.isPending || !!isClosed}
				>
					{t("close_run_agent")}
				</Button>
			</div>
			{/* Confirm close dialog */}
			<AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>{t("close_finalize")}</AlertDialogTitle>
						<AlertDialogDescription>
							{t("confirm_close", { period: periodId })}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>
							{t("cancel", { ns: "common" })}
						</AlertDialogCancel>
						<AlertDialogAction
							onClick={() => {
								closePeriod.mutate({ periodId });
								setConfirmOpen(false);
							}}
						>
							{t("close_finalize")}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>

			{/* Progress bar */}
			{checklist && (
				<div className="flex items-center gap-3">
					<div className="h-2 flex-1 rounded-full bg-muted">
						<div
							className="h-full rounded-full bg-positive transition-all"
							style={{ width: `${checklist.progressPct}%` }}
						/>
					</div>
					<span className="tabular-nums text-sm font-medium">
						{checklist.progressPct}%
					</span>
				</div>
			)}

			{/* Checklist table */}
			<div className="rounded-lg border">
				<DataTable
					columns={checklistCols}
					data={checklistSteps}
					loading={isLoading}
					emptyMessage={t("close_checklist_placeholder", { periodId })}
					className="border-0 rounded-lg"
				/>
			</div>
		</div>
	);
}
